import { supabase } from "@/integrations/supabase/client";

interface ActiveCampaignParams {
  email?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Service for handling Active Campaign events
 */
export const activeCampaignService = {
  /**
   * Called when a user registers
   * Creates a contact in Active Campaign and adds them to the NPO list
   */
  onUserRegister: async ({ email }: ActiveCampaignParams) => {
    try {
      const { data, error } = await supabase.functions.invoke('active-campaign', {
        body: {
          action: 'user',
          email
        }
      });

      if (error) {
        console.error('Failed to register user in Active Campaign:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in onUserRegister:', error);
      throw error;
    }
  },

  /**
   * Called when a user verifies their email
   * Adds the verified tag to the contact in Active Campaign
   */
  onUserVerify: async ({ email }: ActiveCampaignParams) => {
    try {
      const { data, error } = await supabase.functions.invoke('active-campaign', {
        body: {
          action: 'verified'
        }
      });

      if (error) {
        console.error('Failed to mark user as verified in Active Campaign:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in onUserVerify:', error);
      throw error;
    }
  },

  /**
   * Called when a user completes their profile
   * Adds the profile completed tag to the contact in Active Campaign
   */
  onProfileComplete: async ({ email }: ActiveCampaignParams) => {
    try {
      const { data, error } = await supabase.functions.invoke('active-campaign', {
        body: {
          action: 'profile-completed'
        },
      });

      if (error) {
        console.error('Failed to mark profile as completed in Active Campaign:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in onProfileComplete:', error);
      throw error;
    }
  },

  /**
   * Called when a user creates a catalog
   * Adds the catalog created tag to the contact in Active Campaign
   */
  onCatalogCreate: async ({ email }: ActiveCampaignParams) => {
    try {
      const { data, error } = await supabase.functions.invoke('active-campaign', {
        body: {
          action: 'catalog'
        },
      });

      if (error) {
        console.error('Failed to mark catalog as created in Active Campaign:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in onCatalogCreate:', error);
      throw error;
    }
  },

  /**
   * Called when a user subscribes
   * Adds the subscription tag to the contact in Active Campaign
   */
  onSubscribe: async ({ email }: ActiveCampaignParams) => {
    try {
      const { data, error } = await supabase.functions.invoke('active-campaign', {
        body: {
          action: 'subscription'
        },
      });

      if (error) {
        console.error('Failed to mark subscription in Active Campaign:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in onSubscribe:', error);
      throw error;
    }
  },

  /**
   * Called when it's the user's first month of subscription
   * Adds the first month subscription tag to the contact in Active Campaign
   */
  onFirstMonthSubscription: async ({ email }: ActiveCampaignParams) => {
    try {
      const { data, error } = await supabase.functions.invoke('active-campaign', {
        body: {
          action: 'first-month-subscription'
        },
      });

      if (error) {
        console.error('Failed to mark first month subscription in Active Campaign:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in onFirstMonthSubscription:', error);
      throw error;
    }
  },

  /**
   * Called when a user creates a microsite
   * Adds the microsite created tag to the contact in Active Campaign
   */
  onMicrositeCreate: async ({ email }: ActiveCampaignParams) => {
    try {
      const { data, error } = await supabase.functions.invoke('active-campaign', {
        body: {
          action: 'microsite'
        },
      });

      if (error) {
        console.error('Failed to mark microsite as created in Active Campaign:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in onMicrositeCreate:', error);
      throw error;
    }
  },
};
